const BASE = import.meta.env.VITE_API_URL || 'http://localhost:4000';

function tokenHeader() {
  const t = sessionStorage.getItem('m2b_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

async function request(path, opts = {}) {
  const res = await fetch(`${BASE}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...tokenHeader(),
      ...(opts.headers || {}),
    },
  });
  if (res.status === 401) {
    sessionStorage.removeItem('m2b_token');
    sessionStorage.removeItem('m2b_user');
    if (!path.startsWith('/api/v1/auth/') && !path.startsWith('/api/v1/public/')) {
      window.location.href = '/login';
    }
  }
  const text = await res.text();
  const body = text ? JSON.parse(text) : {};
  if (!res.ok) throw Object.assign(new Error(body.message || res.statusText), { status: res.status, body });
  return body;
}

export const api = {
  login:             (email, password) => request('/api/v1/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),

  patients:          (params = {}) => request(`/api/v1/patients?${new URLSearchParams(params)}`),
  patient:           (id) => request(`/api/v1/patients/${id}`),

  prescriptions:     (params = {}) => request(`/api/v1/prescriptions?${new URLSearchParams(params)}`),
  updateRx:          (id, body) => request(`/api/v1/prescriptions/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  deliveries:        (params = {}) => request(`/api/v1/deliveries?${new URLSearchParams(params)}`),
  createDelivery:    (body) => request('/api/v1/deliveries', { method: 'POST', body: JSON.stringify(body) }),
  updateDelivery:    (id, body) => request(`/api/v1/deliveries/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  alerts:            (params = {}) => request(`/api/v1/alerts?${new URLSearchParams(params)}`),
  ackAlert:          (id) => request(`/api/v1/alerts/${id}/acknowledge`, { method: 'POST' }),
  resolveAlert:      (id) => request(`/api/v1/alerts/${id}/resolve`, { method: 'POST' }),

  kpi:               () => request('/api/v1/metrics/kpi'),
  integrationStats:  () => request('/api/v1/metrics/integration'),

  override:          (patientId, body) => request(`/api/v1/admin/patients/${patientId}/override`, { method: 'POST', body: JSON.stringify(body) }),
  overrideLocation:  (deliveryId, body) => request(`/api/v1/admin/deliveries/${deliveryId}/override-location`, { method: 'POST', body: JSON.stringify(body) }),
  auditLog:          () => request('/api/v1/admin/audit'),

  pay:               (body) => request('/api/v1/payments', { method: 'POST', body: JSON.stringify(body) }),
  startCall:         (body) => request('/api/v1/calls', { method: 'POST', body: JSON.stringify(body) }),
  updateCall:        (id, body) => request(`/api/v1/calls/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  simulate:          (body) => request('/api/v1/integration/simulate', { method: 'POST', body: JSON.stringify(body) }),
  intMessages:       () => request('/api/v1/integration/messages'),

  changePassword:    (body) => request('/api/v1/auth/change-password', { method: 'POST', body: JSON.stringify(body) }),

  pmsLookup:         (mrn) => request(`/api/v1/admin/pms/lookup?mrn=${encodeURIComponent(mrn)}`),
  patchPatient:      (id, body) => request(`/api/v1/admin/patients/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  listUsers:         () => request('/api/v1/admin/users'),
  createUser:        (body) => request('/api/v1/admin/users', { method: 'POST', body: JSON.stringify(body) }),
  patchUser:         (id, body) => request(`/api/v1/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  resetUserPassword: (id, body) => request(`/api/v1/admin/users/${id}/reset-password`, { method: 'POST', body: JSON.stringify(body) }),

  branding:          () => request('/api/v1/admin/facility/branding'),
  updateBranding:    (body) => request('/api/v1/admin/facility/branding', { method: 'PATCH', body: JSON.stringify(body) }),

  prescribers:       (patientId) => request(`/api/v1/fhir/patient/${patientId}/prescribers`),
  practitioner:      (npi) => request(`/api/v1/fhir/Practitioner/${npi}`),

  prescribersSummary: () => request('/api/v1/fhir/patients/prescribers-summary'),

  paymentSettings:       ()     => request('/api/v1/admin/facility/payments'),
  updatePaymentSettings: (body) => request('/api/v1/admin/facility/payments', { method: 'PATCH', body: JSON.stringify(body) }),
  stripeOnboard:         ()     => request('/api/v1/admin/facility/payments/stripe/onboard', { method: 'POST' }),

  payInApp:    (body)     => request('/api/v1/payments/in-app',        { method: 'POST', body: JSON.stringify(body) }),
  payText2Pay: (body)     => request('/api/v1/payments/text2pay',      { method: 'POST', body: JSON.stringify(body) }),
  payConsent:  (id, body) => request(`/api/v1/payments/${id}/consent`, { method: 'POST', body: JSON.stringify(body) }),
  payment:     (id)       => request(`/api/v1/payments/${id}`),
  emailReceipt: (id, body) => request(`/api/v1/payments/${id}/email-receipt`, { method: 'POST', body: JSON.stringify(body) }),

  paymentsAudit:       (params = {}) => request(`/api/v1/audit/payments?${new URLSearchParams(params)}`),
  patientTransactions: (patientId, params = {}) =>
    request(`/api/v1/patients/${patientId}/transactions?${new URLSearchParams(params)}`),

  publicLanding: (code) => request(`/api/v1/public/landing${code ? `?code=${encodeURIComponent(code)}` : ''}`),
  publicFaq:     (code) => request(`/api/v1/public/faq${code ? `?code=${encodeURIComponent(code)}` : ''}`),

  // Phase 9 — adherence risk
  watchlist:           (params = {}) => request(`/api/v1/risk/watchlist?${new URLSearchParams(params)}`),
  patientRisk:         (id) => request(`/api/v1/risk/patients/${id}`),
  recomputeRisk:       (id) => request(`/api/v1/risk/recompute/${id}`,    { method: 'POST' }),
  recomputeAllRisk:    ()   => request('/api/v1/risk/recompute-all',      { method: 'POST' }),

  shipments:           (params = {}) => request(`/api/v1/shipments?${new URLSearchParams(params)}`),
  shipment:            (id)          => request(`/api/v1/shipments/${id}`),
  createShipment:      (body)        => request('/api/v1/shipments', { method: 'POST', body: JSON.stringify(body) }),
  shipmentRate:        (id, body)    => request(`/api/v1/shipments/${id}/rate`,        { method: 'POST', body: JSON.stringify(body) }),
  shipmentSelectRate:  (id, body)    => request(`/api/v1/shipments/${id}/select-rate`, { method: 'POST', body: JSON.stringify(body) }),
  shipmentPayment:     (id, body)    => request(`/api/v1/shipments/${id}/payment`,     { method: 'POST', body: JSON.stringify(body) }),
  shipmentBuyLabel:    (id)          => request(`/api/v1/shipments/${id}/label`,       { method: 'POST' }),
  shipmentCancel:      (id)          => request(`/api/v1/shipments/${id}/cancel`,      { method: 'PATCH' }),
};

// ---- Platform / super-admin API ------------------------------------------
function platformTokenHeader() {
  const t = sessionStorage.getItem('m2b_platform_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}
async function platformRequest(path, opts = {}) {
  const res = await fetch(`${BASE}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...platformTokenHeader(), ...(opts.headers || {}) },
  });
  if (res.status === 401) {
    sessionStorage.removeItem('m2b_platform_token');
    sessionStorage.removeItem('m2b_platform_user');
    if (!path.startsWith('/api/v1/platform/auth')) {
      window.location.href = '/platform/login';
    }
  }
  const text = await res.text();
  const body = text ? JSON.parse(text) : {};
  if (!res.ok) throw Object.assign(new Error(body.message || res.statusText), { status: res.status, body });
  return body;
}
export const platformApi = {
  login:        (body) => platformRequest('/api/v1/platform/auth/login', { method: 'POST', body: JSON.stringify(body) }),
  me:           ()     => platformRequest('/api/v1/platform/auth/me'),
  tenants:      ()     => platformRequest('/api/v1/platform/tenants'),
  tenant:       (id)   => platformRequest(`/api/v1/platform/tenants/${id}`),
  createTenant: (body) => platformRequest('/api/v1/platform/tenants', { method: 'POST', body: JSON.stringify(body) }),
  patchTenant:  (id, body) => platformRequest(`/api/v1/platform/tenants/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  softDeleteTenant: (id) => platformRequest(`/api/v1/platform/tenants/${id}`,        { method: 'DELETE' }),
  hardDeleteTenant: (id) => platformRequest(`/api/v1/platform/tenants/${id}?hard=1`, { method: 'DELETE' }),
  impersonate:  (id, body) => platformRequest(`/api/v1/platform/tenants/${id}/impersonate`, { method: 'POST', body: JSON.stringify(body) }),
  audit:        (params = {}) => platformRequest(`/api/v1/platform/audit?${new URLSearchParams(params)}`),
  health:       ()     => platformRequest('/api/v1/platform/health'),

  // Phase 8.3.1
  resetUserPassword: (tenantId, userId, body = {}) =>
    platformRequest(`/api/v1/platform/tenants/${tenantId}/users/${userId}/reset-password`,
                    { method: 'POST', body: JSON.stringify(body) }),
  tenantSecrets:      (tid)        => platformRequest(`/api/v1/platform/tenants/${tid}/secrets`),
  tenantSecretGet:    (tid, key)   => platformRequest(`/api/v1/platform/tenants/${tid}/secrets/${encodeURIComponent(key)}`),
  tenantSecretSet:    (tid, body)  => platformRequest(`/api/v1/platform/tenants/${tid}/secrets`, { method: 'POST', body: JSON.stringify(body) }),
  tenantSecretDelete: (tid, key)   => platformRequest(`/api/v1/platform/tenants/${tid}/secrets/${encodeURIComponent(key)}`, { method: 'DELETE' }),
};

export function connectWS(onMessage) {
  const t = sessionStorage.getItem('m2b_token');
  const base = import.meta.env.VITE_WS_URL || 'ws://localhost:4000/ws';
  const url = `${base}?token=${encodeURIComponent(t || '')}`;
  const ws = new WebSocket(url);
  ws.onmessage = (e) => { try { onMessage(JSON.parse(e.data)); } catch {} };
  ws.onerror = () => {};
  return ws;
}
